import React from 'react';
import { Activity, ArrowRight, TrendingUp } from 'lucide-react';

/**
 * Discrete LEI-index impact of a modeled intervention.
 * Consumes the `lei_impact` block returned by the calculation endpoints
 * (current_lei → projected_lei, +delta, per-pillar breakdown).
 */
export default function LeiImpactCard({ leiImpact, compact = false, testId = 'lei-impact-card', forecastKey = null }) {
  React.useEffect(() => {
    if (forecastKey && leiImpact && leiImpact.lei_delta != null) {
      try { localStorage.setItem(`lei_planned_intervention_${forecastKey}`, String(leiImpact.lei_delta)); } catch (e) { /* ignore */ }
    }
  }, [forecastKey, leiImpact]);
  if (!leiImpact || leiImpact.lei_delta == null) return null;
  const { current_lei, projected_lei, lei_delta, per_pillar = [], has_baseline } = leiImpact;
  const up = lei_delta >= 0;
  const tone = up ? '#059669' : '#e11d48';

  if (compact) {
    return (
      <span
        className="inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-xs font-semibold"
        style={{ backgroundColor: up ? '#ecfdf5' : '#fff1f2', color: tone }}
        data-testid={`${testId}-compact`}
        title={`LEI ${current_lei} → ${projected_lei}`}
      >
        <Activity className="h-3.5 w-3.5" />
        LEI {up ? '+' : ''}{lei_delta} pts
      </span>
    );
  }

  return (
    <div
      className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4"
      data-testid={testId}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <Activity className="h-4 w-4 text-emerald-600" />
          </span>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-emerald-700/80">Projected LEI impact</p>
            <p className="text-sm font-semibold text-slate-700 flex items-center gap-1.5">
              <span className="tabular-nums">{current_lei}</span>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400" />
              <span className="tabular-nums font-extrabold text-emerald-700">{projected_lei}</span>
            </p>
          </div>
        </div>
        <span className="inline-flex items-center gap-1 text-lg font-extrabold tabular-nums" style={{ color: tone }} data-testid={`${testId}-delta`}>
          <TrendingUp className="h-4 w-4" style={{ transform: up ? 'none' : 'scaleY(-1)' }} />
          {up ? '+' : ''}{lei_delta} pts
        </span>
      </div>
      {per_pillar.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`${testId}-pillars`}>
          {per_pillar.map((p) => (
            <span key={p.id} className="inline-flex items-center gap-1 rounded-full border bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600" style={{ borderColor: (p.color || '#cbd5e1') + '66' }}>
              <span className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: p.color || '#94a3b8' }} />
              {p.short_name} <span className="font-bold text-emerald-700">+{p.delta}</span>
            </span>
          ))}
        </div>
      )}
      {leiImpact.capability && (
        <div className="mt-3 pt-3 border-t border-emerald-200/70" data-testid={`${testId}-capability`}>
          <p className="text-[11px] font-bold uppercase tracking-[0.12em] text-indigo-700/80 mb-1.5">Capability propagation (People-Engine)</p>
          <div className="flex flex-wrap gap-1.5">
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">Capability <span className="font-bold text-indigo-700">+{leiImpact.capability.capability_pct}</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">Motivation <span className="font-bold text-indigo-700">+{leiImpact.capability.motivation_pct}</span></span>
            <span className="inline-flex items-center gap-1 rounded-full border border-indigo-200 bg-white px-2 py-0.5 text-[11px] font-medium text-slate-600">PE signal <span className="font-bold text-indigo-700">+{leiImpact.capability.pe_pct}</span></span>
          </div>
          <p className="mt-1 text-[11px] text-slate-500">Modeled People-Engine uplift signal from these LEI improvements. Behaviour/ROE transfer is modelled on the ROE Modeller (needs L2/L3 inputs).</p>
        </div>
      )}
      <p className="mt-2 text-[11px] text-slate-500">
        {has_baseline ? 'Based on your current measured LEI' : 'Estimated from benchmark baselines'} · rolls the measure improvements up to the LEI index (standalone weights).
      </p>
    </div>
  );
}
